import type { SearchEngineEventItem } from '~/composables/types/searchEngine'
import type { EventDescription } from '~/composables/types/event'
import type { TypesenseClientHandle } from '~/composables/search/useSearchClient'
import { useSearchEngine } from '~/composables/search/useSearchEngine'
import { useTypesenseHandle } from '~/composables/search/useTypesenseHandle'
import { fetchTypesenseSourceEvent } from '~/composables/search/useTypesenseSourceEvent'
import { buildSimilarOnlineEventsUrl } from '~/composables/product'
import { kftApiRequest } from '~/utils/kftApiRequest'
import { transformToAlgoliaEvent } from '~/utils/helpers'
import { typesenseSearch } from '~/utils/typesenseClient'
import {
  buildTextSimilarOnlineEventsQuery,
  buildVectorSimilarOnlineEventsQuery,
  type SimilarShowcaseSource,
} from '~/utils/typesenseSimilarEventsQuery'
import { getEventListIndexName } from '~/utils/searchEngineHelpers'
import { toSearchEngineEventItem } from '~/utils/adaptTypesenseEventDocument'

/**
 * Online events within the page's categories, closest by meaning (KFT-4193).
 * Events search from their own stored vector; kits aren't in `events_*`, so
 * they search with their title + subtitle, embedded at query time.
 */
const fetchSimilarOnlineEventsTypesense = async (
  page: SimilarShowcaseSource,
  collection: string,
  handle: TypesenseClientHandle
): Promise<SearchEngineEventItem[]> => {
  let params
  if (page.isKit) {
    if (!page.text) return []
    params = buildTextSimilarOnlineEventsQuery(
      page.text,
      page.categories,
      collection
    )
  } else {
    const source = await fetchTypesenseSourceEvent(
      page.permalink,
      collection,
      handle
    )
    if (!source) return []
    params = buildVectorSimilarOnlineEventsQuery(
      source,
      page.categories,
      collection
    )
  }

  // Static per page, so every viewer of this page sends the same request.
  const result = await typesenseSearch<Record<string, unknown>>(params, {
    ...handle,
    useCache: true,
  })

  if (import.meta.dev) {
    console.debug(
      `[KFT-4193] similar online events (${page.isKit ? 'text' : 'vector'}): ${result.search_time_ms}ms server, ${result.hits?.length ?? 0} hits`
    )
  }

  return (result.hits ?? []).map(({ document }) =>
    toSearchEngineEventItem(document)
  )
}

/**
 * Similar online events for event and kit pages, per the shared search engine
 * (KFT-4115): `algolia` → KFT API online events in the main category,
 * `typesense` → Typesense within the page's categories. The engine is in the
 * key, so an A/B force after hydration refetches from the other backend.
 */
export const useSimilarOnlineEvents = (page: SimilarShowcaseSource) => {
  const { locale } = useI18n()
  const engine = useSearchEngine({ clientOnly: true })
  const typesenseHandle = useTypesenseHandle()

  return useAsyncData<SearchEngineEventItem[]>(
    () => `similar-online-events-${engine.value}-${page.permalink}`,
    async () => {
      // Both engines stay within the page's categories; none → no row.
      if (!page.permalink || !page.categories.main) return []

      if (engine.value === 'typesense') {
        return fetchSimilarOnlineEventsTypesense(
          page,
          getEventListIndexName(locale.value),
          typesenseHandle
        )
      }

      const response = await kftApiRequest<EventDescription[]>(
        buildSimilarOnlineEventsUrl(page.categories.main),
        { method: 'GET' }
      )
      // Same partial shape the KFT API path always produced (useFetch transform).
      return transformToAlgoliaEvent(response) as SearchEngineEventItem[]
    },
    { server: false, default: () => [], dedupe: 'defer' }
  )
}
